/**
 * /admin/system-config — view + edit the infrastructure KV.
 *
 * Layout:
 *   - Left rail: category tabs (llm.qwen / llm.claude / llm.budget / telegram / pipeline)
 *   - Right: list of items in that category, one editable row each
 *
 * Each row shows: 中文 description + key + value editor + last-update meta.
 * Secret rows show "已设置（点编辑覆盖）" placeholder until the operator
 * starts typing — empty submit = no change.
 *
 * 30s cache means changes take effect within 30s on this server instance
 * (and the operator's NEXT request after save sees fresh value because we
 * bust the cache on write).
 */

import { Fragment, useMemo, useState, type FormEvent } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  type ListSystemConfigResponse,
  Routes,
  type SystemConfigItem,
  type TestLlmRequest,
  type TestLlmResponse,
} from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { toastError, toastSuccess } from '../../lib/toast';

const CATEGORY_LABELS: Record<string, string> = {
  'llm.l1': 'L1 向量化（嵌入 0.6B）',
  'llm.l2': 'L2 快筛（轻量 LLM）',
  'llm.l3': 'L3 精筛（强 LLM）',
  'llm.budget': '模型预算与配额',
  telegram: 'Telegram 客户端凭据',
  pipeline: '后台调度参数',
  discovery: '资源发现',
  expansion: '扩散计划',
};

const CATEGORY_HELP: Record<string, string> = {
  'llm.l1':
    'L1 — 把消息转成向量供向量召回用。改"当前后端"立即切换路由；每个后端的 URL/Key/模型独立保存，切回时不需要重填。',
  'llm.l2':
    'L2 — 4B 级轻量模型快速判断"是不是销售线索"。可在本地 Qwen 4B 与 DeepSeek 之间切换；切换只换后端，配置互不影响。',
  'llm.l3':
    'L3 — 强模型精筛 + 结构化打标。可在本地 Qwen 14B / DeepSeek / Claude 之间切换；选谁就读谁的密钥与模型。',
  'llm.budget': '模型每日费用上限。本地 Qwen 是零成本，这些主要影响 DeepSeek / Claude / Voyage 等付费后端。',
  telegram:
    'Telegram MTProto 连接所需的 API ID/Hash + 设备指纹。**五项设备指纹必须与签发会话凭据时完全一致**，否则号会被 TG 注销。改完需要重启服务才完全生效。',
  pipeline:
    '后台调度的轮询间隔和总开关。"自动外联调度器禁用"是支持热加载的紧急熔断开关。',
  discovery:
    '资源发现（抓用户简介 / 解析频道群 / 扫描群成员）的限速与深度。全部走观察号；每日预算按账号计，改动即时生效，不用重启。',
  expansion:
    '资源池里的群怎么变成监听群：打分门槛、自动/人工模式、每号每天加几个、每群几个观察号、全局上限、考核天数与线索门槛、黑名单/加分关键词、加群时段。',
};

/** L1/L2/L3 分组的"当前后端选择"下拉应当被特别突出。 */
const LAYER_BACKEND_KEY: Record<string, string> = {
  'llm.l1': 'llm.l1.backend',
  'llm.l2': 'llm.l2.backend',
  'llm.l3': 'llm.l3.backend',
};

/** 给定 L 层 key 与该层当前 backend，提取 key 中的 backend 段（若有）。 */
function backendOfKey(key: string): string | null {
  // 形如 llm.l2.qwen.url → 'qwen'；llm.l2.backend → null
  const parts = key.split('.');
  if (parts.length >= 4 && parts[0] === 'llm' && /^l\d$/.test(parts[1] ?? '')) {
    return parts[2] ?? null;
  }
  return null;
}

export function SystemConfigPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<string>('llm.l1');

  const list = useQuery({
    queryKey: ['system-config'],
    queryFn: () =>
      api.get<ListSystemConfigResponse>(Routes.admin.systemConfig),
    refetchInterval: 60_000,
  });

  const byCategory = useMemo(() => {
    const out: Record<string, SystemConfigItem[]> = {};
    for (const i of list.data?.items ?? []) {
      (out[i.category] ??= []).push(i);
    }
    return out;
  }, [list.data]);

  const categories = useMemo(() => {
    const all = new Set([...Object.keys(CATEGORY_LABELS), ...Object.keys(byCategory)]);
    return Array.from(all);
  }, [byCategory]);

  // L 层 "已保存的后端" —— 即真实在用的路由目标。
  const savedBackend = useMemo<string | null>(() => {
    const selKey = LAYER_BACKEND_KEY[tab];
    if (!selKey) return null;
    const row = (byCategory[tab] ?? []).find((i) => i.key === selKey);
    return row?.value?.trim() || null;
  }, [tab, byCategory]);

  // 用户在下拉里"挑了一个但还没点保存"的预览值。点保存才会真正切换路由；
  // 预览只决定下面显示哪个后端的字段（让用户先看/填那份配置）。
  const [previewBackendByTab, setPreviewBackendByTab] = useState<
    Record<string, string>
  >({});
  // 切到一个新 tab、或后端选择器被外部改写（已保存）时，预览跟随真实值。
  const previewBackend =
    previewBackendByTab[tab] !== undefined
      ? previewBackendByTab[tab]
      : savedBackend;

  const visibleItems = useMemo(() => {
    const items = byCategory[tab] ?? [];
    const selKey = LAYER_BACKEND_KEY[tab];
    if (!selKey || !previewBackend) return items;
    // 优先显示 backend 选择行，再显示预览 backend 的字段，其他 backend 折叠在底部。
    const selector = items.filter((i) => i.key === selKey);
    const active = items.filter(
      (i) => i.key !== selKey && backendOfKey(i.key) === previewBackend,
    );
    return [...selector, ...active];
  }, [tab, byCategory, previewBackend]);

  const collapsedItems = useMemo(() => {
    const items = byCategory[tab] ?? [];
    const selKey = LAYER_BACKEND_KEY[tab];
    if (!selKey || !previewBackend) return [];
    return items.filter((i) => {
      if (i.key === selKey) return false;
      const b = backendOfKey(i.key);
      return b !== null && b !== previewBackend;
    });
  }, [tab, byCategory, previewBackend]);

  const [showCollapsed, setShowCollapsed] = useState(false);

  // L 层测试结果（按 layer key 存）。null = 没测过；undefined = 正在测。
  const [testResult, setTestResult] = useState<
    Record<string, TestLlmResponse | undefined>
  >({});
  const testMutation = useMutation({
    mutationFn: (req: TestLlmRequest) =>
      api.post<TestLlmResponse>(Routes.admin.systemConfigTestLlm, req),
    onSuccess: (r) => {
      setTestResult((m) => ({ ...m, [`llm.${r.layer}`]: r }));
    },
    onError: (e) => toastError(e),
  });
  const runTest = () => {
    const layer = tab === 'llm.l1' ? 'l1' : tab === 'llm.l2' ? 'l2' : 'l3';
    setTestResult((m) => ({ ...m, [tab]: undefined }));
    testMutation.mutate({ layer, backend: previewBackend ?? undefined });
  };
  const currentTest = testResult[tab];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">系统配置</h1>
        <p className="text-sm text-slate-500 mt-1">
          系统级基础设施配置 — 模型端点 / TG 凭据 / 调度参数。这里改的值在进程里缓存 30 秒，
          最坏情况下 30 秒后所有调用都会用新值（保存后本进程立即生效）。带 <span className="badge-warn">重启生效</span> 标签的项需要重启服务才能完全生效。
        </p>
      </div>

      {list.isLoading ? (
        <div className="text-slate-500 text-sm">加载中…</div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          <aside className="col-span-3 space-y-1">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setTab(cat)}
                className={`block w-full text-left px-3 py-2 rounded text-sm transition ${
                  tab === cat
                    ? 'bg-indigo-600 text-white'
                    : 'hover:bg-slate-100 text-slate-700'
                }`}
              >
                <div className="font-medium">{CATEGORY_LABELS[cat] ?? cat}</div>
                <div className="text-xs opacity-75 font-mono">{cat}</div>
              </button>
            ))}
          </aside>
          <main className="col-span-9 space-y-3">
            {CATEGORY_HELP[tab] && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded">
                {CATEGORY_HELP[tab]}
              </div>
            )}
            {LAYER_BACKEND_KEY[tab] && previewBackend && (
              <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded">
                <div className="text-xs text-slate-600">
                  用「
                  <span className="font-mono text-indigo-700">
                    {previewBackend}
                  </span>
                  」当前填写的配置发一次真实请求，看是否能正常响应。不会改变路由。
                </div>
                <button
                  type="button"
                  className="btn-secondary text-xs whitespace-nowrap"
                  onClick={runTest}
                  disabled={testMutation.isPending}
                >
                  {testMutation.isPending ? '测试中…' : '测试当前后端'}
                </button>
              </div>
            )}
            {currentTest !== undefined && (
              <TestResultPanel result={currentTest} />
            )}
            {visibleItems.map((item, idx) => {
              const isSelector = item.key === LAYER_BACKEND_KEY[tab];
              // 在"当前预览后端字段块"的第一条字段前插一个 banner。
              const isFirstActive =
                LAYER_BACKEND_KEY[tab] !== undefined &&
                previewBackend !== null &&
                idx === 1 &&
                backendOfKey(item.key) === previewBackend;
              return (
                <Fragment key={item.key}>
                  {isFirstActive && (
                    <div className="text-xs text-slate-500 px-1 pt-1 flex items-center gap-2 flex-wrap">
                      <span>正在编辑后端</span>
                      <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-mono">
                        {previewBackend}
                      </span>
                      <span>的配置 — 切换后端不会丢失这里填的值。</span>
                      {previewBackend !== savedBackend && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          仅预览：当前路由仍指向「{savedBackend ?? '未设置'}
                          」，要真正切换请到上方按"保存"
                        </span>
                      )}
                    </div>
                  )}
                  <ConfigRow
                    item={item}
                    highlight={isSelector}
                    onSaved={() => {
                      // 保存成功 → 真实值变了 → 让 preview 跟随真实值（清掉本地覆盖）。
                      if (isSelector) {
                        setPreviewBackendByTab((m) => {
                          const next = { ...m };
                          delete next[tab];
                          return next;
                        });
                      }
                      qc.invalidateQueries({ queryKey: ['system-config'] });
                    }}
                    onDraftChange={
                      isSelector
                        ? (v) =>
                            setPreviewBackendByTab((m) => ({ ...m, [tab]: v }))
                        : undefined
                    }
                  />
                </Fragment>
              );
            })}
            {visibleItems.length === 0 && (
              <div className="text-slate-500 text-sm">该分组下还没有配置项</div>
            )}
            {collapsedItems.length > 0 && (
              <div className="pt-2">
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  onClick={() => setShowCollapsed((v) => !v)}
                >
                  {showCollapsed ? '收起' : '展开'}其他后端的配置（
                  {collapsedItems.length}）
                </button>
                {showCollapsed && (
                  <div className="space-y-3 mt-3">
                    {collapsedItems.map((item) => (
                      <ConfigRow
                        key={item.key}
                        item={item}
                        onSaved={() => {
                          qc.invalidateQueries({ queryKey: ['system-config'] });
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
}

function ConfigRow({
  item,
  highlight,
  onSaved,
  onDraftChange,
}: {
  item: SystemConfigItem;
  highlight?: boolean;
  onSaved: () => void;
  /** Bubble up the in-progress draft so the parent can react before save. */
  onDraftChange?: (value: string) => void;
}) {
  const [draft, setDraft] = useState<string>(
    item.valueMasked ? '' : (item.value ?? ''),
  );
  const [touched, setTouched] = useState(false);

  const save = useMutation({
    mutationFn: (value: string | null) =>
      api.patch(Routes.admin.systemConfigItem(item.key), { value }),
    onSuccess: () => {
      toastSuccess(`已保存 ${item.key}`);
      setTouched(false);
      onSaved();
    },
    onError: (e) => toastError(e),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // For secrets: empty submit = keep existing (server handles).
    // For non-secret: empty submit = clear (set NULL).
    save.mutate(draft === '' ? '' : draft);
  };

  return (
    <form
      onSubmit={onSubmit}
      className={`card border-l-4 ${
        highlight ? 'border-indigo-500 bg-indigo-50/30' : 'border-slate-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-700">{item.key}</div>
          <p className="text-xs text-slate-500 mt-1 whitespace-pre-line">
            {item.description}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-[10px] text-slate-400">
          <span
            className={
              item.valueType === 'secret'
                ? 'badge-fail'
                : item.valueType === 'boolean'
                  ? 'badge-ok'
                  : item.valueType === 'number'
                    ? 'badge-warn'
                    : 'badge-neutral'
            }
          >
            {valueTypeLabel(item.valueType)}
          </span>
          {!item.hotReload && (
            <span className="badge-warn" title="改这一项需要重启服务才完全生效">
              重启生效
            </span>
          )}
          {item.hotReload && (
            <span
              className="text-[10px] text-slate-400"
              title="30 秒缓存到期后自动生效"
            >
              热加载
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-2 items-start">
        {renderInput(item, draft, (v) => {
          setDraft(v);
          setTouched(true);
          onDraftChange?.(v);
        })}
        <button
          type="submit"
          className="btn-primary text-xs"
          disabled={save.isPending || (!touched && item.valueType === 'secret')}
        >
          {save.isPending ? '保存中…' : '保存'}
        </button>
      </div>
      <div className="text-[10px] text-slate-400 mt-2">
        最近更新：{new Date(item.updatedAt).toLocaleString()}
        {item.valueMasked && (
          <span className="ml-2 text-amber-600">
            · 已设置密文（留空表示保持不变）
          </span>
        )}
        {!item.valueMasked && !item.hasValue && (
          <span className="ml-2 text-slate-400">· 未设置</span>
        )}
      </div>
    </form>
  );
}

function valueTypeLabel(t: SystemConfigItem['valueType']): string {
  switch (t) {
    case 'secret':
      return '密文';
    case 'boolean':
      return '开关';
    case 'number':
      return '数值';
    case 'enum':
      return '选项';
    case 'string':
    default:
      return '文本';
  }
}

function renderInput(
  item: SystemConfigItem,
  value: string,
  onChange: (v: string) => void,
) {
  if (item.valueType === 'enum') {
    const opts = item.allowedValues ?? [];
    return (
      <select
        className="input text-sm flex-1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {opts.length === 0 && <option value="">（未配置选项）</option>}
        {opts.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (item.valueType === 'boolean') {
    return (
      <select
        className="input text-sm flex-1"
        value={value || 'false'}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="false">false（关）</option>
        <option value="true">true（开）</option>
      </select>
    );
  }
  if (item.valueType === 'secret') {
    return (
      <input
        type="password"
        className="input text-sm flex-1 font-mono"
        value={value}
        placeholder={item.valueMasked ? '已设置（要覆盖请直接输入新值）' : '未设置'}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
      />
    );
  }
  if (item.valueType === 'number') {
    return (
      <input
        type="number"
        className="input text-sm flex-1 font-mono"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  // string
  return (
    <input
      type="text"
      className="input text-sm flex-1 font-mono"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function TestResultPanel({ result }: { result: TestLlmResponse }) {
  const ok = result.ok;
  return (
    <div
      className={`border rounded text-xs ${
        ok
          ? 'border-emerald-300 bg-emerald-50'
          : 'border-rose-300 bg-rose-50'
      }`}
    >
      <div
        className={`px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 ${
          ok ? 'text-emerald-800' : 'text-rose-800'
        }`}
      >
        <span className="font-semibold">
          {ok ? '✓ 调用成功' : '✗ 调用失败'}
        </span>
        <span>
          后端：<span className="font-mono">{result.backend}</span>
        </span>
        {result.model && (
          <span>
            模型：<span className="font-mono">{result.model}</span>
          </span>
        )}
        <span>耗时：{result.durationMs} ms</span>
        {result.inputTokens !== null && (
          <span>
            tokens：{result.inputTokens} in / {result.outputTokens ?? 0} out
            {typeof result.costUsd === 'number' && result.costUsd > 0 && (
              <> · 费用 ${result.costUsd.toFixed(6)}</>
            )}
          </span>
        )}
      </div>

      {result.error && (
        <pre className="px-3 pb-3 text-rose-900 whitespace-pre-wrap break-all font-mono text-[11px]">
          {result.error}
        </pre>
      )}

      {ok && result.layer === 'l1' && result.vectorDim !== null && (
        <div className="px-3 pb-3 text-emerald-900 space-y-1">
          <div>
            返回向量维度：
            <span className="font-mono font-semibold">{result.vectorDim}</span>
          </div>
          {result.vectorPreview && (
            <div className="font-mono text-[10px] break-all opacity-80">
              前 8 维：[{result.vectorPreview.join(', ')}…]
            </div>
          )}
        </div>
      )}

      {ok && (result.layer === 'l2' || result.layer === 'l3') && (
        <div className="px-3 pb-3 space-y-2">
          {result.prompt && (
            <details>
              <summary className="cursor-pointer text-slate-600">
                测试消息 / 提示词
              </summary>
              <pre className="mt-1 p-2 bg-white border border-slate-200 rounded font-mono text-[11px] whitespace-pre-wrap">
                {result.prompt}
              </pre>
            </details>
          )}
          {result.parsedJudgment && (
            <div className="grid grid-cols-2 gap-2 text-emerald-900">
              <div>
                isLead：
                <span className="font-mono">
                  {String(result.parsedJudgment.isLead)}
                </span>
              </div>
              <div>
                类目：
                <span className="font-mono">
                  {result.parsedJudgment.category ?? 'null'}
                </span>
              </div>
              <div>
                置信度：
                <span className="font-mono">
                  {result.parsedJudgment.confidence.toFixed(2)}
                </span>
              </div>
              <div>
                意图：
                <span className="font-mono">
                  {result.parsedJudgment.intent ?? 'null'}
                </span>
              </div>
              {result.parsedJudgment.tags.length > 0 && (
                <div className="col-span-2">
                  标签：
                  <span className="font-mono">
                    [{result.parsedJudgment.tags.join(', ')}]
                  </span>
                </div>
              )}
              {result.parsedJudgment.suggestedOpener && (
                <div className="col-span-2">
                  建议开场白：
                  <span className="font-mono">
                    {result.parsedJudgment.suggestedOpener}
                  </span>
                </div>
              )}
              {result.parsedJudgment.rationale && (
                <div className="col-span-2 text-slate-600">
                  理由：{result.parsedJudgment.rationale}
                </div>
              )}
            </div>
          )}
          {result.rawResponse && (
            <details>
              <summary className="cursor-pointer text-slate-600">
                原始响应 JSON
              </summary>
              <pre className="mt-1 p-2 bg-white border border-slate-200 rounded font-mono text-[11px] whitespace-pre-wrap">
                {result.rawResponse}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
